<template>
  <div class="a-select" :class="classesAselect" :data-c-tooltip="tooltip" ref="aSelect">
    <div class="c-row" :class="classesRow" @click.stop="show" @keydown="handleKeyDown($event)">
      <column>
        <search-input
          v-if="shouldShowSearchBox"
          v-model="q"
          @blur="blur"
          @focus="handleOnFocus"
          @keydown.esc="resetSelect"
          @reset="resetSelect"
          ref="searchInput"
          :id="$attrs.id"
          :name="$attrs.name"
          :focus="autoFocus"
          :compress="compress"
          :placeholder="searchPlaceholder"
          :disabled="disabled"
          :autocomplete="autocomplete"
          :readonly="disabled"
          v-bind="ariaInput"
        >
        </search-input>
        <button
          v-else
          :class="classesBtn"
          @blur="blurIfNotSearchable"
          @keydown.esc="blurIfNotSearchable"
          @focus="$emit('focus', $event)"
          @mouseenter="$emit('mouseenter', $event)"
          @mouseleave="onMouseleave"
          class="btn-select dropdown-toggle"
          type="button"
          aria-haspopup="true"
          :id="$attrs.id"
          :name="$attrs.name"
          :aria-expanded="isOpen"
          :disabled="disabled"
          v-bind="ariaInput"
          ref="dropdown">

          <template v-if="missingOptionErrorMessage">
            <span class="error-text">
              {{ missingOptionErrorMessage }}
            </span>
          </template>
          <template v-else>
            <template v-if="btnIcon !== ''">
              <icon :class="btnIcon"></icon>
            </template>

            <template v-if="!selectedLabelSuffix && !selectedIcon">
              <slot name="label">
                {{selectedLabel}}
              </slot>
            </template>

            <template v-else>
              <prefix v-if="selectedIcon">
                <icon :class="selectedIcon"></icon>
              </prefix>
              <column ref="textDisplay" class="textDisplay">
                {{selectedLabel}}
              </column>
              <help-tooltip class="d-md-none " :text="selectedLabelSuffix"></help-tooltip>
              <column class="v-option-suffix d-none d-md-block">
                <small>
                  {{selectedLabelSuffix}}
                </small>
              </column>
            </template>
          </template>
        </button>
      </column>
    <slot name="barAction"></slot>
    </div>

    <ul class="dropdown-menu" :class="{show: isOpen}" @keydown="handleKeyDown($event)">
      <template v-if="isOpen && !hideResults && (hasQ || hasOptions || hasPinnedOptions)">
        <li style="margin: 0 10px 6px 10px;">
          <button v-if="showCreateApp" class="btn btn-outline-primary btn-block btn-sm" @mousedown="createApp">Create App</button>
        </li>
        <li v-if="showCreateApp" class="dropdown-divider"></li>
        <template v-for="(set, i) in vOptionSetManager.sets">
          <li v-if="i > 0" class="dropdown-divider"></li>
          <template v-if="set.heading">
            <li :class="selectedColor(set)">
              <h6>{{set.heading}}</h6>
              <ul>
                <template v-if="set.hasOptions">
                  <li v-for="(option, j) in set.options"
                      :key="option[set.altKey]">
                    <button class="dropdown-item"
                            :class="[{active: isHighlighted(i, j), selected: isSelectable && isSelected(option), disabled: readonly}, option.btnClass]"
                            :disabled="readonly"
                            @blur="blur"
                            @mousedown="handleOptionSelect"
                            @click.capture="handleOptionSelectCapture"
                            @keydown.esc="blur"
                            @focus="vOptionSetManager.setHighlightedIndex(i, j)"
                            @mouseover="vOptionSetManager.setHighlightedIndex(i, j)">

                      <template v-if="!option.labelSuffix && !set.icon && !option.icon && !option.avatar">
                        {{set.getLabel(option)}}
                      </template>

                      <template v-else>
                        <row>
                          <prefix v-if="option.icon || set.icon">
                            <icon :class="[option.icon || set.icon]"></icon>
                          </prefix>

                          <prefix v-else-if="option.avatar" class="mr-1">
                            <Avatar :user="option.avatar" />
                          </prefix>

                          <column>
                            {{set.getLabel(option)}}
                          </column>

                          <column type="Bootstrap" v-if="option.labelSuffix" class="v-option-suffix">
                            {{option.labelSuffix}}
                          </column>
                        </row>
                      </template>
                    </button>
                    <button
                      v-if="set.actionButton"
                      :class="{disabled: readonly}"
                      :disabled="readonly"
                      class="action"
                      @mousedown="emitAction"
                      @click="emitAction"
                    >
                      {{set.getActionButtonLabel(option)}}
                    </button>
                  </li>
                </template>
                <li v-else-if="set.showWhenEmpty"
                    class="dropdown-item">
                  {{set.searchStatus}}
                </li>
              </ul>
            </li>
          </template>

          <template v-else>
            <template v-if="set.hasOptions">
              <li v-if="set.getLabel(option)"
                  v-for="(option, j) in set.options"
                  :class="selectedColor(set)"
                  :key="option[set.altKey]">
                <button class="dropdown-item"
                        :class="[{active: isHighlighted(i, j), selected: isSelectable && isSelected(option) && !showCheckboxes, disabled: readonly}, option.btnClass]"
                        :disabled="readonly"
                        @blur="blur"
                        @mousedown="handleOptionSelect"
                        @click.capture="handleOptionSelectCapture"
                        @keydown.esc="blur"
                        @focus="vOptionSetManager.setHighlightedIndex(i, j)"
                        @mouseover="vOptionSetManager.setHighlightedIndex(i, j)">

                  <template v-if="!option.labelSuffix && !(icon = set.iconKey ? set.getIcon(option) : set.icon) && !option.avatar">
                    <input v-if="showCheckboxes" type="checkbox" @click.prevent @mousedown.prevent :checked="vOptionSetManager.isSelected(option)">
                    {{set.getLabel(option)}}
                  </template>

                  <template v-else>
                    <row class="align-items-center">
                      <input v-if="showCheckboxes" type="checkbox" @click.prevent @mousedown.prevent :checked="vOptionSetManager.isSelected(option)">

                      <prefix v-if="icon = set.iconKey ? set.getIcon(option) : set.icon">
                        <icon :class="icon"></icon>
                      </prefix>

                      <prefix v-else-if="option.avatar" class="mr-1">
                        <Avatar :user="option.avatar" />
                      </prefix>

                      <column>
                        {{set.getLabel(option)}}
                      </column>

                      <column type="Bootstrap" v-if="option.labelSuffix" class="v-option-suffix">
                        {{option.labelSuffix}}
                      </column>
                    </row>
                  </template>
                </button>
                <button
                  v-if="set.actionButton"
                  :class="{disabled: readonly}"
                  :disabled="readonly"
                  class="action"
                  @mousedown="emitAction"
                  @click="emitAction"
                >
                  {{set.getActionButtonLabel(option)}}
                </button>
              </li>
            </template>
            <li v-else-if="set.showWhenEmpty"
                class="dropdown-item">
              {{set.searchStatus}}
            </li>
          </template>
        </template>
      </template>
    </ul>

  </div>
</template>

<script lang="ts">
  import Vue from "vue";
  import aSelectMixin from "./a-select-mixin";

  export default Vue.extend({
    mixins: [aSelectMixin],
  });
</script>